import { c as _c } from "react/compiler-runtime";
import { queryOptions, useMutation, useQueryClient } from "@tanstack/react-query";
import type { MarketingExport, MarketingExportCreate, MarketingPhotoConsent } from "@/types/photo-studio";
import { requestData } from "@/api/api-config";
import { patientQueryKeys } from "@/api/provider/user/patient/patient-query";
export const marketingQueryKeys = {
  all: ["photo-studio", "marketing"] as const,
  consent: (patientId: string) => [...marketingQueryKeys.all, "consent", patientId] as const
};
export async function getMarketingPhotoConsent(patientId: string) {
  return await requestData<MarketingPhotoConsent>(`photo-studio/marketing/consent/${patientId}`, "get");
}
export async function updateMarketingPhotoConsent(patientId: string, consented: boolean) {
  return await requestData<MarketingPhotoConsent>(`photo-studio/marketing/consent/${patientId}`, "put", {
    json: {
      consented
    }
  }, false);
}

// catchError=false: a 403 MARKETING_CONSENT_REQUIRED is shown inline in the dialog.
export async function createMarketingExport(request: MarketingExportCreate) {
  return await requestData<MarketingExport>("photo-studio/marketing/exports", "post", {
    json: request,
    timeout: 120_000
  }, false);
}

// A fresh, short-lived link to one export. The server re-checks consent, so a
// withdrawn patient's export gets a 403 MARKETING_CONSENT_REQUIRED instead.
// catchError=false so the dialog can say why.
export async function getMarketingExportLink(exportId: string, options: {
  download?: boolean;
} = {}) {
  return await requestData<MarketingExport>(`photo-studio/marketing/exports/${exportId}/link`, "get", {
    searchParams: {
      download: options.download ?? false
    }
  }, false);
}
export const marketingQueryOptions = {
  consent: (patientId: string) => queryOptions({
    queryKey: marketingQueryKeys.consent(patientId),
    queryFn: () => getMarketingPhotoConsent(patientId)
  })
};
export function useUpdateMarketingConsentMutation(patientId) {
  const $ = _c(8);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== patientId) {
    t0 = consented => updateMarketingPhotoConsent(patientId, consented);
    $[0] = patientId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== patientId || $[3] !== queryClient) {
    t1 = consent => {
      if (consent) {
        queryClient.setQueryData(marketingQueryKeys.consent(patientId), consent);
      }
      queryClient.invalidateQueries({
        queryKey: patientQueryKeys.detail(patientId)
      });
      queryClient.invalidateQueries({
        queryKey: patientQueryKeys.editHistory(patientId)
      });
    };
    $[2] = patientId;
    $[3] = queryClient;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== t0 || $[6] !== t1) {
    t2 = {
      mutationFn: t0,
      onSuccess: t1
    };
    $[5] = t0;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return useMutation(t2);
}
export function useCreateMarketingExportMutation() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      mutationFn: _temp
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useMutation(t0);
}
function _temp(request) {
  return createMarketingExport(request);
}